import { Box, Button, Checkbox, Divider, Flex, Heading, TextInput, createForm } from '@applyboard/crystal-ui';
import { useNavigate } from 'react-router-dom';
import { UserStateActions, UserWithState } from '../';
import { AgencyData, CreateAgencyProps, ProfileState } from '../../clients';

export type AgencyFormProps = Readonly<{
  agencyData?: AgencyData;
  onSubmit?: (agencyData: CreateAgencyProps) => void;
  isLoading: boolean;
  isReadOnly?: boolean;
}>;

export type AddAgencyFieldValues = {
  name: string;
  institutionSupport: boolean;
};

const { Form, Field } = createForm<AddAgencyFieldValues>();

export function AgencyForm({ agencyData, isReadOnly, onSubmit, isLoading }: AgencyFormProps): JSX.Element {
  const navigate = useNavigate();

  const initialValues: AddAgencyFieldValues = {
    name: agencyData?.name ?? '',
    institutionSupport: agencyData?.institutionSupport ?? false,
  };

  const canManageAgencyState = !isReadOnly && agencyData !== undefined;

  const userStateData: UserWithState | undefined = agencyData
    ? {
        id: agencyData.id,
        givenName: agencyData.name,
        familyName: undefined,
        state: agencyData.enabled ? ProfileState.active : ProfileState.inactive,
      }
    : undefined;

  return (
    <Form
      onSubmit={(formValue) => {
        onSubmit?.({
          name: formValue.name,
          enabled: true,
          institutionSupport: formValue.institutionSupport ? true : undefined,
        });
      }}
      defaultValues={initialValues}
    >
      <Flex py={{ xs: 2, sm: 8 }} justify="between" align="center" gap={10} grow={1}>
        <Flex direction="column" basis="100%">
          <Box pb={8}>
            <Heading level={2}>Agency information</Heading>
          </Box>
          <Box pb={8}>
            <Field
              // @ts-expect-error - Field component type compatibility issue with Crystal UI
              as={TextInput}
              label="Agency Name"
              name="name"
              placeholder="Enter agency name"
              required="Agency name is required."
              disabled={isReadOnly}
            />
          </Box>
          <Box pb={8}>
            <Field
              // @ts-expect-error - Field component type compatibility issue with Crystal UI
              as={Checkbox}
              label="Institution Support"
              name="institutionSupport"
              disabled={isReadOnly}
            />
          </Box>
          {!isReadOnly && (
            <>
              <Box py={{ xs: 8, sm: 10 }}>
                <Divider />
              </Box>
              <Flex justify="end" gap={4}>
                <UserStateActions
                  userData={userStateData}
                  canManageUserState={canManageAgencyState}
                  redirectPath="/profiles/agencies"
                  userType="Agency"
                />
                <Button
                  type="button"
                  onClick={() => {
                    navigate('/profiles/agencies');
                  }}
                  intent="primary"
                  emphasis="outlined"
                >
                  Cancel
                </Button>
                <Button loading={isLoading} type="submit" intent="primary">
                  {agencyData ? 'Update Agency' : 'Add Agency'}
                </Button>
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
    </Form>
  );
}
